import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Award, Plus, Loader2, Star, Trophy, Medal, Heart, Trash2 } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const badgeTypes = [
  { value: 'star', label: 'Étoile', icon: Star, color: 'text-amber-500' },
  { value: 'trophy', label: 'Trophée', icon: Trophy, color: 'text-yellow-500' },
  { value: 'medal', label: 'Médaille', icon: Medal, color: 'text-blue-500' },
  { value: 'heart', label: 'Coeur', icon: Heart, color: 'text-red-500' }
];

export default function RecognitionPage() {
  const { user, token } = useAuth();
  const [recognitions, setRecognitions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ recipient_id: '', message: '', badge_type: 'star' });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [recRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/recognitions`, { headers }),
        axios.get(`${API_URL}/api/chat/users`, { headers })
      ]);
      setRecognitions(recRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.recipient_id || !form.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/recognitions`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reconnaissance envoyée !');
      setDialogOpen(false);
      setForm({ recipient_id: '', message: '', badge_type: 'star' });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette reconnaissance ?')) return;
    try {
      await axios.delete(`${API_URL}/api/recognitions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reconnaissance supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getBadgeIcon = (type) => {
    const badge = badgeTypes.find(b => b.value === type) || badgeTypes[0];
    const Icon = badge.icon;
    return <Icon className={`w-6 h-6 ${badge.color}`} />;
  };

  const myRecognitions = recognitions.filter(r => r.recipient_id === user?.id);
  const givenRecognitions = recognitions.filter(r => r.sender_id === user?.id);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recognition-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reconnaissance</h1>
          <p className="text-slate-500 mt-1">Célébrez les réussites de vos collègues</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" />Envoyer une reconnaissance
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <Star className="w-8 h-8 text-amber-500 mb-2" />
            <p className="text-sm text-amber-700">Reçues</p>
            <p className="text-2xl font-bold text-amber-900">{myRecognitions.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <Award className="w-8 h-8 text-blue-500 mb-2" />
            <p className="text-sm text-blue-700">Envoyées</p>
            <p className="text-2xl font-bold text-blue-900">{givenRecognitions.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <Trophy className="w-8 h-8 text-yellow-500 mb-2" />
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-bold">{recognitions.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <Heart className="w-8 h-8 text-red-500 mb-2" />
            <p className="text-sm text-slate-500">Ce mois</p>
            <p className="text-2xl font-bold">
              {recognitions.filter(r => new Date(r.created_at).getMonth() === new Date().getMonth()).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Wall of recognition */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Mur de reconnaissance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recognitions.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Award className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aucune reconnaissance encore</p>
              <p className="text-sm">Soyez le premier à reconnaître un collègue !</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {recognitions.map((rec) => (
                <div key={rec.id} className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border relative group">
                  {/* Delete button - visible for admin or sender */}
                  {(user?.role === 'admin' || rec.sender_id === user?.id) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                      onClick={() => handleDelete(rec.id)}
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-full bg-white shadow-sm">
                      {getBadgeIcon(rec.badge_type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-slate-900 text-white text-xs">
                            {rec.recipient_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-slate-900">{rec.recipient_name}</p>
                          <p className="text-xs text-slate-500">De {rec.sender_name}</p>
                        </div>
                      </div>
                      <p className="text-slate-700 italic">"{rec.message}"</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {format(new Date(rec.created_at), 'dd MMMM yyyy', { locale: fr })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Envoyer une reconnaissance</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Collègue *</Label>
              <Select value={form.recipient_id} onValueChange={(v) => setForm({ ...form, recipient_id: v })}>
                <SelectTrigger><SelectValue placeholder="Sélectionner un collègue" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Badge</Label>
              <div className="flex gap-2">
                {badgeTypes.map((badge) => {
                  const Icon = badge.icon;
                  return (
                    <button
                      key={badge.value}
                      type="button"
                      onClick={() => setForm({ ...form, badge_type: badge.value })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        form.badge_type === badge.value ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 ${badge.color}`} />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea 
                value={form.message} 
                onChange={(e) => setForm({ ...form, message: e.target.value })} 
                rows={3} 
                placeholder="Pourquoi reconnaissez-vous cette personne ?"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
